import React, { useState } from "react";
import { 
  FiPlus, 
  FiSearch, 
  FiFilter, 
  FiDownload, 
  FiEye, 
  FiChevronLeft, 
  FiChevronRight,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiTag
} from "react-icons/fi";
import type { RcaRecordDto } from "../../../types/rca.types.js";

export type RcaRecordsListPageProps = {
  records: readonly RcaRecordDto[];
  onSelectRecord: (record: RcaRecordDto) => void;
  onCreateNew: () => void;
  isLoading?: boolean;
};

export function RcaRecordsListPage({
  records,
  onSelectRecord,
  onCreateNew,
  isLoading = false,
}: RcaRecordsListPageProps) {
  const [searchTerm, setSearchTerm] = useState<string>("" );
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [methodFilter, setMethodFilter] = useState<string>("ALL");
  const [priorityFilter, setPriorityFilter] = useState<string>("ALL");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 8;

  // Filter records
  const filtered = records.filter(r => {
    const matchesSearch = !searchTerm.trim() || 
      r.rcaCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.complaintCode && r.complaintCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      r.problemStatement.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.ownerName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "ALL" || r.statusCode.toUpperCase() === statusFilter.toUpperCase();
    const matchesMethod = methodFilter === "ALL" || r.rcaMethodCode.toUpperCase() === methodFilter.toUpperCase();
    const matchesPriority = priorityFilter === "ALL" || (r.priorityLevel && r.priorityLevel.toUpperCase() === priorityFilter.toUpperCase());

    return matchesSearch && matchesStatus && matchesMethod && matchesPriority;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const displayed = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(displayed.map(d => d.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleToggleSelectOne = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const getStatusBadge = (status: string) => {
    const s = status.toUpperCase();
    if (s.includes("PROGRESS")) return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200">In Progress</span>;
    if (s.includes("REVIEW")) return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">Review</span>;
    if (s.includes("ACTIVE") || s.includes("INITIATED")) return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-700 border border-cyan-200">Active</span>;
    if (s.includes("COMPLETE") || s.includes("APPROVED")) return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Completed</span>;
    if (s.includes("OVERDUE") || s.includes("REJECT")) return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">Overdue</span>;
    if (s.includes("HOLD")) return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200">On Hold</span>;
    return <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">Draft</span>;
  };

  const getPriorityBadge = (priority?: string) => {
    const p = (priority || "MEDIUM").toUpperCase();
    if (p === "CRITICAL") return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">Critical</span>;
    if (p === "HIGH") return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-orange-50 text-orange-700 border border-orange-200">High</span>;
    if (p === "MEDIUM") return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">Medium</span>;
    return <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">Low</span>;
  };

  const getSlaBadge = (slaText?: string, isOverdue?: boolean) => {
    if (!slaText) return <span className="text-slate-400 text-xs">—</span>;
    if (isOverdue || slaText.includes("-") || slaText.toLowerCase().includes("overdue")) {
      return (
        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
          <FiClock className="w-3 h-3" /> {slaText}
        </span>
      );
    }
    if (slaText.toLowerCase().includes("complete")) {
      return (
        <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          Completed
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-sky-50 text-sky-700 border border-sky-200 flex items-center gap-1">
        <FiClock className="w-3 h-3" /> {slaText}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top Header Card */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; RCA / CAPA</div>
          <h1 className="text-xl font-bold text-slate-900 mt-0.5">RCA Records</h1>
        </div>

        <button
          type="button"
          onClick={onCreateNew}
          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
        >
          <FiPlus className="w-4 h-4" />
          Create New RCA
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
        {/* Search Input */}
        <div className="relative flex-1 min-w-[240px]">
          <FiSearch className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by RCA Code, complaint, problem statement, owner..."
            className="w-full pl-9 pr-4 py-2 text-xs rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Status Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs py-2 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Statuses</option>
            <option value="RCA_INITIATED">Active / Initiated</option>
            <option value="RCA_IN_PROGRESS">In Progress</option>
            <option value="RCA_UNDER_REVIEW">Under Review</option>
            <option value="RCA_COMPLETED">Completed</option>
            <option value="RCA_OVERDUE">Overdue</option>
          </select>

          {/* Method Dropdown */}
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="text-xs py-2 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Methods</option>
            <option value="FIVE_WHY">5-Why Analysis</option>
            <option value="FISHBONE">Fishbone (Ishikawa)</option>
            <option value="FTA">Fault Tree Analysis</option>
            <option value="PARETO">Pareto Analysis</option>
            <option value="8D">8D Problem Solving</option>
            <option value="PROCESS_MAP">Process Mapping</option>
            <option value="IS_IS_NOT">Is / Is Not</option>
            <option value="BRAINSTORMING">Brainstorming</option>
          </select>

          {/* Priority Dropdown */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="text-xs py-2 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 font-medium focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">All Priorities</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>
      </div>

      {/* Main Records Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase tracking-wider text-[11px]">
              <tr>
                <th className="py-3.5 px-4 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === displayed.length && displayed.length > 0}
                    onChange={(e) => handleToggleSelectAll(e.target.checked)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="py-3.5 px-4">RCA Code</th>
                <th className="py-3.5 px-4">Complaint / NCR</th>
                <th className="py-3.5 px-4">Method</th>
                <th className="py-3.5 px-4">Owner</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Priority</th>
                <th className="py-3.5 px-4">Due Date</th>
                <th className="py-3.5 px-4">SLA</th>
                <th className="py-3.5 px-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayed.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-12 text-center text-slate-400">
                    <FiAlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No RCA records match your search or filter criteria.
                  </td>
                </tr>
              ) : (
                displayed.map((record) => {
                  const isSelected = selectedIds.includes(record.id);
                  return (
                    <tr
                      key={record.id}
                      onClick={() => onSelectRecord(record)}
                      className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                        isSelected ? "bg-blue-50/40" : ""
                      }`}
                    >
                      <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectOne(record.id)}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="py-3.5 px-4 font-bold text-blue-600">
                        {record.rcaCode}
                      </td>
                      <td className="py-3.5 px-4 text-slate-800 font-medium">
                        <div>{record.complaintCode || "CMP-2026-0001"}</div>
                        <div className="text-[11px] text-slate-400 truncate max-w-[180px]">
                          {record.complaintTitle || record.problemStatement}
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="font-semibold text-slate-700">
                          {record.rcaMethodName}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-slate-600 font-medium">
                        {record.ownerName || "RCA Owner"}
                      </td>
                      <td className="py-3.5 px-4">
                        {getStatusBadge(record.statusName || record.statusCode)}
                      </td>
                      <td className="py-3.5 px-4">
                        {getPriorityBadge(record.priorityLevel)}
                      </td>
                      <td className="py-3.5 px-4 text-slate-600">
                        {record.targetDate ? new Date(record.targetDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "28 Aug 2026"}
                      </td>
                      <td className="py-3.5 px-4">
                        {getSlaBadge(record.slaDaysText, record.isOverdue)}
                      </td>
                      <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => onSelectRecord(record)}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-[11px] font-semibold shadow-sm inline-flex items-center gap-1"
                        >
                          <FiEye className="w-3 h-3" /> View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer with Pagination */}
        <div className="p-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            Showing <span className="font-bold text-slate-700">{(currentPage - 1) * pageSize + 1}</span> to{" "}
            <span className="font-bold text-slate-700">{Math.min(currentPage * pageSize, filtered.length)}</span> of{" "}
            <span className="font-bold text-slate-700">{filtered.length}</span> records
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              className="p-1.5 rounded-lg border border-slate-300 disabled:opacity-40 hover:bg-slate-50 text-slate-700"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 font-semibold text-slate-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              className="p-1.5 rounded-lg border border-slate-300 disabled:opacity-40 hover:bg-slate-50 text-slate-700"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
